import { useState, useCallback } from 'preact/hooks';
import { TabBar } from '../components/TabBar';
import { MobileTopBar } from '../components/MobileTopBar';
import { TodayHeader } from '../components/TodayHeader';
import { TodayChairsView } from './TodayChairsView';
import { GuestInboxView } from './GuestInboxView';
import { GuestDossierDrawer } from '../components/GuestDossierDrawer';
import { TranscriptReplayModal } from '../components/TranscriptReplayModal';
import { SettingsView } from './SettingsView';
import { CardPolicyNotice } from '../components/CardPolicyNotice';
import { ChatView } from './ChatView';
import { DashboardAllowanceNotice } from '../components/AllowanceNotice';

// Stable identity for ChatView's re-entry refineContext. ChatView keys its
// framing effect on refineContext, so a fresh object literal each render would
// wipe the in-progress teaching conversation — this module constant pins it.
const TEACH_CONTEXT = { type: 'general' };

export function MainDashboard({ dashboardData, onBilling, onDataRefresh }) {
  // Design IA (Jonathan.dc.html): Today · Guests · Calendar · Your salon.
  // Each tab body lands in a C2–C5 bead. Today = the chairs panel (C4).
  const [activeTab, setActiveTab] = useState('today');
  // Selected guest for the C3 dossier drawer (bead jonathan-6k07). The inbox
  // (C2) and the today-chairs rows (C4) pass a conversation/thread id up; the
  // drawer fetches that guest's render_stylist_prep dossier and renders Face B.
  // We also stash the inbox name so the drawer header reads instantly, before
  // the fetch resolves (the dossier carries its own client_name as a fallback).
  const [openGuestId, setOpenGuestId] = useState(null);
  const [openGuestName, setOpenGuestName] = useState(null);
  // C5 (bead jonathan-715y): the "See {name}'s side" phone-frame transcript replay.
  // Triggered from the C3 drawer's CTA; replays the stored widget conversation
  // for the same thread. We carry the resolved guest name so the modal's
  // framing copy reads instantly.
  const [replayConversation, setReplayConversation] = useState(null);
  // Persistent re-entry to the owner teaching conversation (bead jonathan-bxgw).
  // Opened from the "Your salon" tab's "Talk to Jonathan" row and from the
  // Today empty-state nudge. Reuses ChatView in refineContext mode so it opens
  // a fresh general teaching turn (no first-run greeting replay) — the same
  // backend /v1/owner/chat, just ongoing instead of first-run.
  const [chatOpen, setChatOpen] = useState(false);

  const openGuest = useCallback((id) => {
    setOpenGuestId(id);
    const row = (dashboardData?.conversations || []).find((c) => c.id === id);
    setOpenGuestName(row?.guest_name || null);
  }, [dashboardData]);

  const closeGuest = useCallback(() => {
    setOpenGuestId(null);
    setOpenGuestName(null);
  }, []);

  const salonName = dashboardData?.salon_name;
  // Real owner name for the Today greeting (resolved server-side; never the
  // business name). Null -> name-less warm greeting (bead jonathan-f7y5).
  const ownerName = dashboardData?.owner_name;

  return (
    <div
      class="min-h-screen"
      style={{ background: 'var(--bg)' }}
      data-open-guest={openGuestId || undefined}
    >
      {/* Mobile top app bar (C8, bead jonathan-ydfw): italic "Jonathan"
          wordmark + salon-initial avatar. The mobile .dc.html ships this above
          the tabbed views; the desktop .dc.html has no top bar, so it is hidden
          on wide viewports via the mobile-only class. The bottom TabBar is the
          other half of the mobile shell and is already responsive (C1). */}
      <div class="mobile-only">
        <MobileTopBar salonName={salonName} />
      </div>
      {dashboardData?.ai_available === false && activeTab !== 'salon' && <p role="status" style={{ padding: '0.75rem 1rem', margin: 0, fontSize: '0.875rem' }}>AI assistance is paused. Your salon settings and billing controls remain available.</p>}

      {/* Today — the mobile Today tab (C8): the greeting + "Your week with
          assistant" masthead (TodayHeader) scrolls above the "Today's chairs"
          panel (C4, bead jonathan-fym6) in one container. The masthead is
          mobile-shaped, so it is passed only as the chairs view's header slot;
          on desktop the chairs panel stands alone. Chairs are wired to GET
          /v1/owner/today-chairs; dossier-linked rows open the C3 drawer on
          their joined thread. Degrades gracefully until the endpoint deploys
          (bead h07w). */}
      {activeTab === 'today' && (
        <TodayChairsView
          onOpenGuest={openGuest}
          onTeachJonathan={() => setChatOpen(true)}
          headerSlot={
            <>
            <CardPolicyNotice required={dashboardData?.card_policy_review_required} disabled={dashboardData?.ai_available === false} onReview={() => setChatOpen(true)} />
            <DashboardAllowanceNotice onReview={onBilling} refreshKey={dashboardData} />
            <div class="mobile-only">
              <TodayHeader
                ownerName={ownerName}
                conversations={dashboardData?.conversations}
              />
            </div>
            </>
          }
        />
      )}

      {/* Guests — the "New from Jonathan" inbox (C2). Tapping a guest opens
          the C3 dossier drawer; the open handler is wired now so the drawer
          lights up the moment C3 lands. */}
      {activeTab === 'guests' && (
        <GuestInboxView
          dashboardData={dashboardData}
          onOpenGuest={openGuest}
        />
      )}

      {/* C3 — guest dossier drawer (Face B). Slides in over any tab when a
          guest is selected; degrades gracefully if the dossier endpoint isn't
          deployed yet (deploy bead h07w). "See {name}'s side" opens the C5 phone-
          frame transcript replay for the same thread. */}
      {openGuestId && (
        <GuestDossierDrawer
          guestId={openGuestId}
          guestName={openGuestName}
          onClose={closeGuest}
          onSeeHerSide={(id, name) =>
            setReplayConversation({ id, name })
          }
        />
      )}

      {/* C5 — "See {name}'s side" phone-frame transcript replay (bead jonathan-715y).
          Sits above the drawer (z-index 80); the stored widget conversation is
          replayed statically. Degrades gracefully until the transcript endpoint
          deploys. */}
      {replayConversation && (
        <TranscriptReplayModal
          conversationId={replayConversation.id}
          guestName={replayConversation.name}
          onClose={() => setReplayConversation(null)}
        />
      )}

      {/* Calendar — placeholder until the calendar view is built. */}
      {activeTab === 'calendar' && (
        <div class="min-h-screen flex items-center justify-center px-6 pb-28">
          <div class="text-center max-w-sm">
            <div class="font-serif text-2xl mb-2" style={{ color: 'var(--ink)' }}>
              Calendar
            </div>
            <div class="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Today's chairs and the week ahead are coming here.
            </div>
          </div>
        </div>
      )}

      {/* Your salon — settings, menu, and knowledge controls. Carries the
          persistent "Talk to" re-entry into the teaching conversation
          (bead jonathan-bxgw). */}
      {activeTab === 'salon' && (
        <SettingsView
          onBilling={onBilling}
          dashboardData={dashboardData}
          onTeachJonathan={() => setChatOpen(true)}
        />
      )}

      {/* Owner teaching conversation, re-homed (bead jonathan-bxgw). Reused
          full-screen over the dashboard with a close affordance; refineContext
          opens a fresh general teaching turn instead of replaying the first-run
          greeting. Same /v1/owner/chat backend. */}
      {chatOpen && (
        <div class="fixed inset-0" style={{ zIndex: 70 }}>
          <ChatView
            dashboardData={dashboardData}
            refineContext={TEACH_CONTEXT}
            onClose={() => { setChatOpen(false); onDataRefresh?.(); }}
          />
        </div>
      )}

      <TabBar activeTab={activeTab} onTabChange={setActiveTab} />
    </div>
  );
}
